import { isNil, startCase, toLower } from 'lodash-es';

/**
 * Singularizes a word based on common English pluralization patterns.
 * @param word - The word to singularize.
 * @returns The singularized form of the input word.
 */
export function singularize(word: string) {
	if (word.endsWith('ies')) {
		return `${word.slice(0, -3)}y`;
	}
	if (word.endsWith('es')) {
		return word.slice(0, -2);
	}
	if (word.endsWith('s')) {
		return word.slice(0, -1);
	}
	return word;
}

/**
 * Pluralizes a word based on common English pluralization patterns.
 * @param word - The word to pluralize.
 * @param count - The count associated with the word (optional).
 * @param inclusive - Whether to include the count in the result (optional).
 * @returns The pluralized form of the input word, optionally including the count.
 */
export function pluralize(word: string, count?: number, inclusive?: boolean) {
	let result = word;

	if (isNil(count) || count > 1) {
		if (word.toLowerCase().endsWith('s')) {
			result = word;
		} else if (word.toLowerCase().endsWith('y')) {
			if (word === 'day') {
				result = 'days';
			} else {
				result = `${word.slice(0, -1)}ies`;
			}
		} else {
			result = `${word}s`;
		}
	}

	if (word === 'selected') {
		result = 'selected';
	}

	if (inclusive) {
		result = `${count} ${result}`;
	}

	return result;
}

/**
 * Converts a string to sentence case (lowercase with the first letter of each word capitalized).
 * Uses lodash startCase and toLower functions.
 * @param str - The string to convert to sentence case.
 * @returns The input string converted to sentence case.
 */
export function sentenceCase(str: string): string {
	return startCase(toLower(str));
}

/**
 * Truncates a string if it exceeds a maximum length and adds an ellipsis.
 * @param text - The string to truncate.
 * @param maxLength - The maximum allowed length of the string.
 * @returns The truncated string with an ellipsis if it exceeds maxLength, otherwise the original string.
 */
export function truncateWithEllipsis(text: string, maxLength: number): string {
	// Check if the text length is greater than the maximum length
	if (text.length > maxLength) {
		// Truncate the text and add an ellipsis
		return `${text.substring(0, maxLength - 1)}…`;
	}

	// Return the original text if it's within the length limit
	return text;
}

/**
 * Converts a string to Title Case format where the first letter of each word is capitalized
 * and the rest are lowercase.
 *
 * @example
 * titleCase("hello world") // returns "Hello World"
 * titleCase("HELLO WORLD") // returns "Hello World"
 * titleCase("hello_world") // returns "Hello_World"
 *
 * @param str - The input string to convert
 * @returns The string converted to title case
 */
export function titleCase(str?: string | null | undefined): string {
	if (isNil(str)) {
		return '';
	}
	return str.replace(
		/\w\S*/g,
		(txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
	);
}

/**
 * Replaces ampersands (&) with the word "and" in a string.
 *
 * @example
 * replaceAmpersandWithAnd("Sales & Marketing") // returns "Sales and Marketing"
 * replaceAmpersandWithAnd("R&D") // returns "R and D"
 *
 * @param str - The input string to convert
 * @returns The string with ampersands replaced by "and"
 */
export function replaceAmpersandWithAnd(
	str?: string | null | undefined
): string {
	if (isNil(str)) {
		return '';
	}
	return str.replace(/&/g, ' and ');
}
